/**
 * Before a form starts: ask staff for the values of the template's `input`
 * variables (fields with data_source "variable.<key>"), which the API uses to
 * prefill the new instance (`variable_inputs`).
 */

import React, { useState } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  VariableInputFields,
  cleanInputs,
  missingRequiredInputs,
  type RequiredInput,
} from '@/components/messaging/templateVariables';

interface FormStartInputsDialogProps {
  inputs: RequiredInput[];
  onCancel: () => void;
  onConfirm: (variableInputs: Record<string, string> | undefined) => void;
}

export function FormStartInputsDialog({ inputs, onCancel, onConfirm }: FormStartInputsDialogProps) {
  const { t } = useAppTranslation();
  const [values, setValues] = useState<Record<string, string>>({});
  const missing = missingRequiredInputs(inputs, values);

  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('templateVariables.forms.startTitle', 'Before you start this form')}</DialogTitle>
          <DialogDescription>
            {t('templateVariables.forms.startDescription', 'This form fills some fields from values you enter here.')}
          </DialogDescription>
        </DialogHeader>
        <VariableInputFields inputs={inputs} values={values} onChange={setValues} idPrefix="form-start-input" />
        {missing.length > 0 && (
          <p className="text-xs text-ink-500">
            {t('templateVariables.inputs.missing', 'Fill in the required values: {{labels}}', {
              labels: missing.map((m) => m.label || m.key).join(', '),
            })}
          </p>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>
            {t('templateVariables.form.cancel', 'Cancel')}
          </Button>
          <Button type="button" disabled={missing.length > 0} onClick={() => onConfirm(cleanInputs(values))}>
            {t('templateVariables.forms.start', 'Start form')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
